import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { Heading } from '../layout';
import { UsersForm } from '../forms/UsersForm';
import UsersTable3 from './UsersTable3';
import { apiClient, API_BASE_URL } from '../../services/apiClient';
import AdministrationBackLink from '../system/AdministrationBackLink';

const EMPTY_FORM = {
  user_code: '',
  full_name: '',
  email: '',
  job_title: '',
  fono: '',
  status: 'Activo',
  password: '',
  password_confirm: '',
  role_ids: [],
};

const SystemUsersWorkspace = ({ isDarkMode = false, showAdminBack = false }) => {
  const [usersData, setUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [roleOptions, setRoleOptions] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const rawData = await apiClient(`${API_BASE_URL}/api/users/`);
      setUsersData(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setUsersError('No se pudo cargar usuarios desde la base de datos.');
      setUsersData([]);
      console.error('Error loading users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const roles = await apiClient(`${API_BASE_URL}/api/rbac/roles`);
      setRoleOptions(Array.isArray(roles) ? roles : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoleOptions([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const openCreateForm = () => {
    setEditingUserId(null);
    setFormError('');
    setFormData({ ...EMPTY_FORM, user_code: 'Se asigna automaticamente', role_ids: [] });
    setShowFormModal(true);
  };

  const openEditForm = (userRow) => {
    setEditingUserId(userRow.id);
    setFormError('');
    setFormData({
      ...EMPTY_FORM,
      user_code: userRow.user_code || '',
      full_name: userRow.full_name || '',
      email: userRow.email || '',
      job_title: userRow.job_title || '',
      fono: userRow.fono || '',
      status: userRow.status || 'Activo',
      password: '',
      password_confirm: '',
      role_ids: Array.isArray(userRow.roles) ? userRow.roles.map((r) => Number(r.id)) : [],
    });
    setShowFormModal(true);
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) return 'El nombre es obligatorio.';
    if (!formData.email.trim()) return 'El correo es obligatorio.';
    if (!String(formData.job_title || '').trim()) return 'El cargo es obligatorio.';

    const password = String(formData.password || '');
    const passwordConfirm = String(formData.password_confirm || '');
    const isEditing = Boolean(editingUserId);

    if (!isEditing) {
      if (!password) return 'La contraseña es obligatoria.';
      if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
      if (password !== passwordConfirm) return 'Las contraseñas no coinciden.';
      return '';
    }

    if (password || passwordConfirm) {
      if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
      if (password !== passwordConfirm) return 'Las contraseñas no coinciden.';
    }
    return '';
  };

  const handleSaveUser = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSaving(true);
    setFormError('');

    const payload = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      job_title: String(formData.job_title || '').trim(),
      fono: formData.fono.trim(),
      status: formData.status || 'Activo',
      role_ids: Array.isArray(formData.role_ids) ? formData.role_ids.map(Number) : [],
    };
    const password = String(formData.password || '').trim();
    if (password) {
      payload.password = password;
    }

    const endpoint = editingUserId
      ? `${API_BASE_URL}/api/users/${editingUserId}`
      : `${API_BASE_URL}/api/users/`;
    const method = editingUserId ? 'PUT' : 'POST';

    try {
      await apiClient(endpoint, {
        method,
        body: JSON.stringify(payload),
      });
      setShowFormModal(false);
      setEditingUserId(null);
      setFormData({ ...EMPTY_FORM });
      await loadUsers();
    } catch (error) {
      setFormError(error.message || 'No se pudo guardar el usuario.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUsers = async (payload) => {
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    await Promise.all(
      rowsToDelete.map((row) =>
        apiClient(`${API_BASE_URL}/api/users/${row.id}`, { method: 'DELETE' })
      )
    );
    await loadUsers();
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        <Users size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Usuarios del Sistema
      </Heading>
      {showAdminBack ? <AdministrationBackLink /> : null}

      <UsersTable3
        rows={usersData}
        loading={usersLoading}
        error={usersError}
        isDarkMode={isDarkMode}
        tableTitle="Usuarios"
        addButtonLabel="Agregar usuario"
        onAdd={openCreateForm}
        onRowClick={openEditForm}
        onBulkDelete={handleDeleteUsers}
      />

      <UsersForm
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSave={handleSaveUser}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingUserId)}
        formData={formData}
        onChange={handleFormChange}
        formError={formError}
        roleOptions={roleOptions}
      />
    </div>
  );
};

export default SystemUsersWorkspace;
